import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Fuel,
  MapPin,
  X,
  CheckCircle,
  Check,
  Gauge,
  Container,
  Truck,
  Navigation,
  ChevronDown,
  Building2,
  Banknote,
  QrCode,
  UploadCloud,
  Menu,
  Camera,
  ArrowRight,
  Zap,
  History,
  Calendar,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import RefuelLoader from "../components/Refuelloader.jsx";
import Swal from "sweetalert2";
import api, { IMAGE_BASE_URL, ADMIN_IMAGE_URL } from "../api/Api";

// --- IMAGE COMPRESSION UTILITY ---
const compressImage = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const MAX_WIDTH = 1024;
        const scaleSize = MAX_WIDTH / img.width;
        const newWidth = img.width > MAX_WIDTH ? MAX_WIDTH : img.width;
        const newHeight =
          img.width > MAX_WIDTH ? img.height * scaleSize : img.height;
        canvas.width = newWidth;
        canvas.height = newHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, newWidth, newHeight);
        ctx.canvas.toBlob(
          (blob) => {
            if (!blob) return reject(new Error("Compression failed"));
            resolve(
              new File([blob], file.name, {
                type: "image/jpeg",
                lastModified: Date.now(),
              }),
            );
          },
          "image/jpeg",
          0.6,
        );
      };
      img.onerror = (err) => reject(err);
    };
    reader.onerror = (err) => reject(err);
  });
};

const RefuelPage = () => {
  // --- Refs ---
  const refs = {
    pumpMeter: useRef(null),
    physicalTank: useRef(null),
    vehicleMeter: useRef(null),
    paymentQr: useRef(null),
  };
  const navigate = useNavigate();
  // --- State ---
  const [loading, setLoading] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [fetchingLocation, setFetchingLocation] = useState(false);
  const [activeTrips, setActiveTrips] = useState([]);
  const [tripsLoading, setTripsLoading] = useState(false);

  // History State
  const [logs, setLogs] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [fullImage, setFullImage] = useState(null);

  const [form, setForm] = useState({
    refuelContext: "duty",
    selectedTripId: "",
    confirmationId: "",
    location: "",
    latitude: "",
    longitude: "",
    liters: "",
    pricePerLiter: "",
    amount: "",
    pumpMeterImg: null,
    pumpMeterPreview: null,
    physicalTankImg: null,
    physicalTankPreview: null,
    vehicleMeterImg: null,
    vehicleMeterPreview: null,
    paymentQrImg: null,
    paymentQrPreview: null,
    paymentMode: "office",
  });

  // --- 1. INITIAL DATA FETCHING ---
  useEffect(() => {
    fetchActiveTrips();
    fetchHistory();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  // --- FETCH HISTORY ---

  const fetchActiveTrips = async () => {
    const driverRegNo = localStorage.getItem("driverRegNo");
    if (!driverRegNo) return;

    setTripsLoading(true);
    try {
      const response = await api.get(
        `/driver/select-trips-expense?driver_regno=${driverRegNo}`,
      );

      const tripData = response.data || [];
      const validTrips = tripData.filter((trip) => trip && trip.enq_id);
      setActiveTrips(validTrips);
    } catch (error) {
      console.error("Trip fetch error", error);
      setActiveTrips([]);
    } finally {
      setTripsLoading(false);
    }
  };

  const fetchHistory = async () => {
    const driverRegNo = localStorage.getItem("driverRegNo");
    if (!driverRegNo) return;
    try {
      setHistoryLoading(true);
      const response = await api.get(`/refuel/history/${driverRegNo}`);
      //console.log("History response", response);
      if (response.data.success) {
        setLogs(response.data.data);
      }
    } catch (error) {
      console.error("History fetch error", error);
    } finally {
      setHistoryLoading(false);
    }
  };

  // --- 2. LOCATION ---
  const handleGetLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    setFetchingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setForm((prev) => ({ ...prev, latitude, longitude }));
        const fetchAddress = async () => {
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error("TIMEOUT")), 5000),
          );
          const apiCall = api.post("/location/geocode", {
            latitude,
            longitude,
          });
          return Promise.race([apiCall, timeoutPromise]);
        };
        try {
          const res = await fetchAddress();
          if (res.data.success) {
            setForm((prev) => ({ ...prev, location: res.data.address }));
            toast.success("Pump Location found!");
          } else {
            setForm((prev) => ({
              ...prev,
              location: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
            }));
            toast.warning("Address not found, using coords.");
          }
        } catch (error) {
          setForm((prev) => ({
            ...prev,
            location: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
          }));
          toast.success("GPS Coords captured.");
        } finally {
          setFetchingLocation(false);
        }
      },
      () => {
        toast.error("Unable to get location");
        setFetchingLocation(false);
      },
      { enableHighAccuracy: true },
    );
  };

  // --- 3. AUTO CALCULATION ---
  // const handleCalc = (field, value) => {
  //   // 1. Create a copy of the current form to calculate against
  //   const nextForm = {
  //     ...form,
  //     [field]: value,
  //   };
  //   // 2. Parse numbers safely (handle empty strings as 0 for calculation)
  //   const l = parseFloat(nextForm.liters) || 0;
  //   const p = parseFloat(nextForm.pricePerLiter) || 0;
  //   // 3. Calculate Amount
  //   const totalAmount = Math.round(l * p * 100) / 100;
  //   // 4. Update State once
  //   setForm({
  //     ...nextForm,
  //     amount: totalAmount > 0 ? totalAmount.toFixed(2) : "",
  //   });
  // };

  const handleCalc = (field, value) => {
    setForm((prev) => {
      const updatedForm = {
        ...prev,
        [field]: value,
      };

      const liters = parseFloat(updatedForm.liters) || 0;
      const rate = parseFloat(updatedForm.pricePerLiter) || 0;
      const totalAmount = liters * rate;

      return {
        ...updatedForm,
        amount: totalAmount > 0 ? totalAmount.toFixed(2) : "",
      };
    });
  };

  // --- 4. IMAGE HANDLING ---
  const handleImageUpload = async (e, fieldKey) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setCompressing(true);
      const compressed = await compressImage(file);
      setForm((prev) => ({
        ...prev,
        [`${fieldKey}Img`]: compressed,
        [`${fieldKey}Preview`]: URL.createObjectURL(compressed),
      }));
      toast.success("Photo captured");
    } catch {
      toast.error("Photo failed");
    } finally {
      setCompressing(false);
    }
  };

  const removeImage = (fieldKey) => {
    setForm((prev) => ({
      ...prev,
      [`${fieldKey}Img`]: null,
      [`${fieldKey}Preview`]: null,
    }));
  };

  // --- 5. SUBMIT ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    const driverRegNo = localStorage.getItem("driverRegNo");

    if (!form.location) return toast.error("Pump Location required");
    if (!form.liters || !form.pricePerLiter)
      return toast.error("Fuel details required");
    if (form.refuelContext === "duty" && !form.selectedTripId)
      return toast.error("Please select a Trip");
    if (!form.pumpMeterImg || !form.physicalTankImg || !form.vehicleMeterImg)
      return toast.error("All 3 meter photos are required");
    // if (form.paymentMode === "office" && !form.paymentQrImg)
    //   return toast.error("QR Code required for Office payment");

    setLoading(true);
    const formData = new FormData();
    formData.append("driver_id", driverRegNo);
    formData.append("refuel_type", form.refuelContext);
    formData.append(
      "trip_id",
      form.refuelContext === "duty" ? form.selectedTripId : "",
    );
    formData.append(
      "confirmation_id",
      form.refuelContext === "duty" ? form.confirmationId : "",
    );
    formData.append("location_name", form.location);
    formData.append("latitude", form.latitude);
    formData.append("longitude", form.longitude);
    formData.append("liters", form.liters);
    formData.append("rate", form.pricePerLiter);
    formData.append("total_amount", form.amount);
    formData.append("payment_mode", form.paymentMode);

    formData.append("pump_meter_image", form.pumpMeterImg);
    formData.append("tank_image", form.physicalTankImg);
    formData.append("vehicle_meter_image", form.vehicleMeterImg);
    if (form.paymentQrImg)
      formData.append("payment_qr_image", form.paymentQrImg);

    try {
      const res = await api.post("/refuel/add-fuelexpence", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.success) {
        Swal.fire({
          icon: "success",
          title: "Refuel Expense Successful",
          text:
            form.paymentMode === "office"
              ? "Request sent to Office"
              : "Cash payment logged",
          confirmButtonColor: "#0d6efd",
          width: "280px",
          padding: "1.25rem 1rem 1rem",
          customClass: {
            popup: "rounded-4 shadow-lg border-0",
            icon: "m-0 mx-auto",
            title: "fs-6 fw-bold mt-3 mb-1",
            htmlContainer: "m-0 mb-3 small text-muted",
            actions: "w-100 m-0 p-0",
            confirmButton: "w-100 rounded-pill py-2 m-0 fw-bold",
          },
          backdrop: "rgba(0,0,0,0.6)",
        });

        setForm({
          refuelContext: "duty",
          selectedTripId: "",
          confirmationId: "",
          location: "",
          latitude: "",
          longitude: "",
          liters: "",
          pricePerLiter: "",
          amount: "",
          pumpMeterImg: null,
          pumpMeterPreview: null,
          physicalTankImg: null,
          physicalTankPreview: null,
          vehicleMeterImg: null,
          vehicleMeterPreview: null,
          paymentQrImg: null,
          paymentQrPreview: null,
          paymentMode: "office",
        });

        fetchHistory();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  // const getImageUrl = (filename) => {
  //   if (!filename) return "";
  //   return `${IMAGE_BASE_URL}/uploads/refuel_receipt/${filename}`;
  // };
  const getImageUrl = (filename, type) => {
    if (!filename) {
      return "https://placehold.co/400?text=No+Img";
    }
    let finalUrl = "";
    if (type === "payment_proof") {
      finalUrl = `${ADMIN_IMAGE_URL}/uploads/refuel_receipt/${filename}`;
    } else {
      finalUrl = `${IMAGE_BASE_URL}/uploads/refuel_receipt/${filename}`;
    }
    return finalUrl;
  };

  // --- UI COMPONENTS ---
  const SquareUploader = ({ label, fKey, Icon, color }) => (
    <div
      className="position-relative bg-white border border-light-subtle rounded-4 shadow-sm overflow-hidden d-flex flex-column align-items-center justify-content-center cursor-pointer active-scale"
      style={{ aspectRatio: "1/1" }}
      onClick={() => refs[fKey].current.click()}
    >
      <input
        type="file"
        hidden
        ref={refs[fKey]}
        accept="image/*"
        capture="environment"
        onChange={(e) => handleImageUpload(e, fKey)}
      />
      {form[`${fKey}Preview`] ? (
        <>
          <img
            src={form[`${fKey}Preview`]}
            className="w-100 h-100 object-fit-cover"
            alt={label}
          />
          <div
            className="position-absolute top-0 end-0 bg-danger text-white p-1 m-1 rounded-circle shadow"
            onClick={(e) => {
              e.stopPropagation();
              removeImage(fKey);
            }}
          >
            {" "}
            <X size={14} />{" "}
          </div>
        </>
      ) : (
        <div className={`text-center ${color}`}>
          <div className="bg-light rounded-circle p-2 mb-2 d-inline-block">
            {" "}
            <Icon size={24} className="opacity-75" />{" "}
          </div>
          <div className="fw-bold small text-dark opacity-75">{label}</div>
        </div>
      )}
      {!form[`${fKey}Preview`] && (
        <div className="position-absolute bottom-0 end-0 m-2 text-muted opacity-50">
          {" "}
          <Camera size={14} />{" "}
        </div>
      )}
    </div>
  );

  if (loading || historyLoading) {
    return (
      <div className="loader-container">
        <RefuelLoader />
        <style>{`
            .loader-container {
                position: fixed; 
                top: 0;
                left: 0;
                width: 100%;
                height: 100%; 
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                background: #f8fafc;
            }
        `}</style>
      </div>
    );
  }

  return (
    <div className="container-fluid p-0 bg-white-subtle min-vh-100 pb-5 font-native">
      {/* --- HEADER --- */}
      <div
        className="position-relative bg-primary pb-5 rounded-bottom-4 shadow-sm overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #00ccff 0%, #ff6600 100%)",
        }}
      >
        <Fuel
          size={180}
          className="position-absolute text-white opacity-10"
          style={{ right: -40, top: -20, transform: "rotate(15deg)" }}
        />
        <div className="d-flex align-items-center justify-content-between p-4 pt-4">
          <div className="d-flex align-items-center gap-3">
            <h4 className="text-white fw-bold mb-0 tracking-tight text-uppercase">
              Add Fuel Expense
            </h4>
          </div>
        </div>
      </div>

      <div className="px-3" style={{ marginTop: "-45px" }}>
        <form onSubmit={handleSubmit} className="d-flex flex-column gap-3">
          {/* 1. DUTY TOGGLE */}
          <div className="card border-0 shadow rounded-4 p-1 bg-white">
            <div
              className="d-flex position-relative bg-light rounded-pill p-1"
              style={{ height: "48px" }}
            >
              <div
                className="position-absolute transition-all"
                style={{
                  top: "4px",
                  bottom: "4px",
                  width: "calc(50% - 4px)",
                  left: form.refuelContext === "duty" ? "4px" : "50%",
                  transition: "left 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)",
                }}
              />
              <div
                onClick={() => setForm({ ...form, refuelContext: "duty" })}
                className={`flex-grow-1 d-flex align-items-center justify-content-center gap-1 cursor-pointer z-1 ${
                  form.refuelContext === "duty"
                    ? "text-primary fw-bold"
                    : "text-muted fw-medium"
                }`}
                style={{ fontSize: "0.90rem" }}
              >
                <Truck size={16} />{" "}
                <span className="text-nowrap">Putting Oil for Duty</span>
              </div>
            </div>
            {form.refuelContext === "duty" && (
              <div className="mx-2 mb-2 mt-2 animate-slide-down">
                <div className="position-relative">
                  <select
                    className="form-select border-0 bg-light fw-bold py-3 ps-3 pe-5 rounded-4 shadow-inner"
                    value={form.selectedTripId}
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      const selectedTrip = activeTrips.find(
                        (t) => Number(t.enq_id) === Number(selectedId),
                      );

                      setForm({
                        ...form,
                        selectedTripId: selectedId,
                        confirmationId: selectedTrip
                          ? selectedTrip.confirmation_id
                          : "",
                      });
                    }}
                    style={{ fontSize: "0.9rem" }}
                    disabled={tripsLoading}
                  >
                    <option value="" style={{ color: "#333" }}>
                      {tripsLoading ? "Loading Trips..." : "-- Select Trip --"}
                    </option>

                    {!tripsLoading && activeTrips.length > 0 ? (
                      activeTrips.map((trip, index) => {
                        const optionColor =
                          trip.trip_category === "Active"
                            ? "#198754"
                            : "#0d6efd";

                        return (
                          <option
                            key={`${trip.enq_id}-${index}`}
                            value={trip.enq_id}
                            style={{
                              color: optionColor,
                              fontSize: "13px",
                              fontWeight: "600",
                            }}

                            // disabled={trip.trip_category === "Old"}
                          >
                            {trip.confirmation_id || "Unknown"}
                            {` (${trip.trip_category}) `}
                            {` | ${formatDate(
                              trip.Pickup_time,
                            )} to ${formatDate(trip.dropoff_time)}`}
                          </option>
                        );
                      })
                    ) : !tripsLoading ? (
                      <option disabled value="">
                        No Trips Found
                      </option>
                    ) : null}
                  </select>

                  {/* 5. Loading Spinner vs Arrow */}
                  <div className="position-absolute top-50 end-0 translate-middle-y me-3 pointer-events-none">
                    {tripsLoading ? (
                      <div className="spinner-border spinner-border-sm text-primary"></div>
                    ) : (
                      <ChevronDown size={20} className="text-muted" />
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 2. LOCATION & DETAILS */}
          <div className="card border-0 shadow-sm rounded-4 p-3 bg-white">
            <div className="mb-3">
              <label className="text-dark small fw-bold ms-1 mb-1">
                LOCATION OF PUMP
              </label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control bg-light border-0 fw-bold py-3 rounded-start-4 ps-3"
                  placeholder="Pump Location..."
                  value={form.location}
                  onChange={(e) =>
                    setForm({ ...form, location: e.target.value })
                  }
                />
                <button
                  type="button"
                  className="btn btn-warning fw-bold text-dark rounded-end-4 px-3 d-flex align-items-center gap-2"
                  onClick={handleGetLocation}
                  disabled={fetchingLocation}
                >
                  {fetchingLocation ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    <>
                      {" "}
                      <MapPin size={18} /> Fetch{" "}
                    </>
                  )}
                </button>
              </div>
            </div>
            <div className="row g-2">
              {/* LITERS INPUT */}
              <div className="col-4">
                <label className="text-muted small fw-bold ms-1">LITERS</label>
                <input
                  type="number"
                  inputMode="decimal"
                  className="form-control bg-light border-0 fw-bold py-3 rounded-4 text-center"
                  placeholder="0"
                  value={form.liters}
                  onChange={(e) => handleCalc("liters", e.target.value)}
                  onWheel={(e) => e.target.blur()}
                />
              </div>

              {/* PRICE PER LITER INPUT */}
              <div className="col-4">
                <label className="text-muted small fw-bold ms-1">RATE/L</label>
                <input
                  type="number"
                  inputMode="decimal"
                  className="form-control bg-light border-0 fw-bold py-3 rounded-4 text-center"
                  placeholder="0"
                  value={form.pricePerLiter}
                  onChange={(e) => handleCalc("pricePerLiter", e.target.value)}
                  onWheel={(e) => e.target.blur()}
                />
              </div>

              {/* TOTAL AMOUNT (READ ONLY) */}
              <div className="col-4">
                <label className="text-success small fw-bold ms-1">TOTAL</label>
                <div className="form-control bg-success bg-opacity-10 border-0 fw-bold py-3 rounded-4 text-center text-success">
                  {form.amount || 0}
                </div>
              </div>
            </div>
          </div>

          {/* 3. METER PHOTOS */}
          <div>
            <div className="d-flex align-items-center justify-content-between mb-2 px-1">
              {" "}
              <span className="text-dark fw-bold small tracking-wide">
                {" "}
                METER READINGS{" "}
              </span>{" "}
              <span className="badge bg-danger-subtle text-danger rounded-pill border">
                {" "}
                Photos Required{" "}
              </span>{" "}
            </div>
            <div className="row g-3">
              <div className="col-4">
                {" "}
                <SquareUploader
                  label="Pump"
                  fKey="pumpMeter"
                  Icon={Gauge}
                  color="text-primary"
                />{" "}
              </div>
              <div className="col-4">
                {" "}
                <SquareUploader
                  label="Tank"
                  fKey="physicalTank"
                  Icon={Container}
                  color="text-dark"
                />{" "}
              </div>
              <div className="col-4">
                {" "}
                <SquareUploader
                  label="Odo"
                  fKey="vehicleMeter"
                  Icon={Truck}
                  color="text-warning"
                />{" "}
              </div>
            </div>
          </div>

          {/* 4. PAYMENT & QR */}
          <div className="card border-0 shadow-sm rounded-4 overflow-hidden bg-white mt-2">
            <div className="bg-light p-3 pb-2">
              {" "}
              <span className="fw-bold text-dark small">
                PAYMENT METHOD
              </span>{" "}
            </div>
            <div className="p-2">
              <div className="row g-2">
                <div className="col-6">
                  <div
                    onClick={() => setForm({ ...form, paymentMode: "office" })}
                    className={`p-2 rounded-4 border text-center cursor-pointer transition-all active-scale d-flex flex-column align-items-center gap-1 ${
                      form.paymentMode === "office"
                        ? "bg-primary text-white border-primary shadow-sm"
                        : "bg-white text-muted border-light"
                    }`}
                  >
                    {" "}
                    <Building2 size={24} />{" "}
                    <span className="fw-bold small">Submit to Office</span>{" "}
                  </div>
                </div>
                <div className="col-6">
                  <div
                    onClick={() => setForm({ ...form, paymentMode: "cash" })}
                    className={`p-2 rounded-4 border text-center cursor-pointer transition-all active-scale d-flex flex-column align-items-center gap-1 ${
                      form.paymentMode === "cash"
                        ? "bg-success text-white border-success shadow-sm"
                        : "bg-white text-muted border-light"
                    }`}
                  >
                    {" "}
                    <Banknote size={24} />{" "}
                    <span className="fw-bold small">Pay Via Cash</span>{" "}
                  </div>
                </div>
              </div>
            </div>
            {form.paymentMode === "office" && (
              <div className="px-3 pb-3 animate-slide-down">
                <div
                  className="position-relative bg-light border border-2 border-dashed border-primary rounded-4 d-flex flex-column align-items-center justify-content-center cursor-pointer"
                  style={{ height: "180px" }}
                  onClick={() => refs.paymentQr.current.click()}
                >
                  <input
                    type="file"
                    hidden
                    ref={refs.paymentQr}
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, "paymentQr")}
                  />
                  {form.paymentQrPreview ? (
                    <>
                      <img
                        src={form.paymentQrPreview}
                        className="w-100 h-100 object-fit-contain rounded-4 p-2"
                        alt="QR"
                      />
                      <div
                        className="position-absolute top-0 end-0 m-2 bg-dark text-white p-1 rounded-circle"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeImage("paymentQr");
                        }}
                      >
                        {" "}
                        <X size={16} />{" "}
                      </div>
                    </>
                  ) : (
                    <div className="text-center text-primary">
                      <div className="bg-white p-3 rounded-circle shadow-sm mb-2 d-inline-block">
                        {" "}
                        <QrCode size={32} />{" "}
                      </div>
                      <div className="fw-bold">Upload QR Code</div>
                      <div className="small opacity-75">Tap to upload</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* SUBMIT BUTTON */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={loading || compressing}
              className={`btn btn-lg w-100 rounded-pill fw-bold py-2 shadow-lg d-flex align-items-center justify-content-center gap-2 active-scale ${
                form.paymentMode === "office" ? "btn-primary" : "btn-success"
              }`}
            >
              {loading ? (
                <span className="spinner-border spinner-border-sm" />
              ) : form.paymentMode === "office" ? (
                <Zap size={20} />
              ) : (
                <CheckCircle size={20} />
              )}
              {form.paymentMode === "office"
                ? "SUBMIT TO OFFICE"
                : "PAY VIA CASH"}
            </button>
          </div>
        </form>

        {/* --- HISTORY LIST SECTION --- */}
        <div className="mt-5 mb-5 pb-5">
          <div className="d-flex align-items-center justify-content-between mb-3 px-1 border-bottom pb-2">
            <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2 tracking-wide">
              <History size={18} className="text-primary" /> RECENT REFUELS
            </h6>

            {/* LINK TO HISTORY PAGE */}
            <div
              onClick={() => navigate("/refuel-history")}
              className="d-flex align-items-center gap-1 text-info fw-bold small cursor-pointer active-scale"
            >
              View All <ArrowRight size={16} />
            </div>
          </div>
          {historyLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary spinner-border-sm"></div>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-5 text-muted opacity-50 small">
              No recent refuel history
            </div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {/* slice(0, 5) limits items to 5 */}
              {logs.slice(0, 5).map((log) => (
                <div
                  key={log.id}
                  onClick={() => setSelectedLog(log)}
                  className="card border-0 shadow-sm rounded-4 position-relative overflow-hidden active-scale-down cursor-pointer"
                >
                  <div
                    className={`position-absolute top-0 bottom-0 start-0 w-1 bg-${log.status_color}`}
                    style={{ width: "6px" }}
                  ></div>

                  {/* Added flex-wrap and gap-2 for better mobile responsiveness */}
                  <div className="p-3 ps-4 d-flex flex-wrap align-items-center justify-content-between gap-2">
                    <div
                      className="d-flex align-items-center gap-3 flex-grow-1"
                      style={{ minWidth: 0 }}
                    >
                      <div
                        className={`rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 bg-${log.status_color}-subtle text-${log.status_color}`}
                        style={{ width: "45px", height: "45px" }}
                      >
                        <Fuel size={20} />
                      </div>

                      {/* Added style minWidth: 0 to allow text-truncate to work inside flexbox */}
                      <div style={{ minWidth: 0 }}>
                        <h6
                          className="fw-bold mb-0 text-dark text-truncate small text-uppercase"
                          style={{ maxWidth: "150px" }}
                        >
                          {log.station_name}
                        </h6>

                        <small
                          className="text-dark d-flex align-items-center gap-1 mb-1"
                          style={{ fontSize: "0.75rem" }}
                        >
                          <Calendar size={12} />
                          {new Date(log.date).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "2-digit",
                          })}
                        </small>

                        {/* NEW: Badges for Cash Payment and Internal Notes */}
                        <div className="d-flex flex-wrap gap-1 mt-1">
                          {Number(log.paid_thru) === 16 && (
                            <span
                              className="badge bg-info-subtle text-success border border-info-subtle"
                              style={{ fontSize: "0.65rem" }}
                            >
                              CASH PAYMENT
                            </span>
                          )}

                          {log.internal_notes && (
                            <span
                              className="badge bg-info-subtle text-success border border-info-subtle text-truncate"
                              style={{ maxWidth: "120px", fontSize: "0.65rem" }}
                              title={log.internal_notes}
                            >
                              {log.internal_notes}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="text-end flex-shrink-0">
                      <h6 className="fw-bold mb-1 text-dark">₹{log.amount}</h6>
                      <span
                        className={`badge rounded-pill bg-${log.status_color}-subtle text-${log.status_color} border border-${log.status_color}-subtle`}
                        style={{ fontSize: "0.65rem" }}
                      >
                        {log.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ============ TRANSACTION DETAILS MODAL ============ */}
      {selectedLog && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.75)",
            zIndex: 99999,
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
            // backdropFilter: "blur(5px)",
            transform: "translateZ(0)",
            WebkitTransform: "translateZ(0)",
          }}
          onClick={() => setSelectedLog(null)}
        >
          <div
            className="bg-white w-100 shadow-lg animate-slide-up"
            style={{
              maxWidth: "500px",
              maxHeight: "92%",
              overflowY: "auto",
              borderTopLeftRadius: "28px",
              borderTopRightRadius: "28px",
              position: "relative",
              zIndex: 100000,
              transform: "translateZ(0)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Draggable Indicator */}
            <div className="d-flex justify-content-center pt-3 pb-2">
              <div
                className="bg-secondary opacity-25 rounded-pill"
                style={{ width: "45px", height: "5px" }}
              ></div>
            </div>

            {/* Header */}
            <div className="px-4 pb-3 d-flex justify-content-between align-items-center border-bottom border-light-subtle sticky-top bg-white">
              <div>
                <h5 className="fw-bold mb-0 text-dark">Refuel Details</h5>
                <small
                  className="text-primary text-uppercase fw-semibold"
                  style={{ letterSpacing: "1px" }}
                >
                  {selectedLog.expense_no}
                </small>
              </div>
              <button
                className="btn btn-light rounded-circle p-2 shadow-sm"
                onClick={() => setSelectedLog(null)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="px-4 pb-4 pt-3">
              {/* Status Hero Card */}
              <div
                className="p-4 rounded-4 mb-4 text-white shadow-sm position-relative overflow-hidden"
                style={{
                  background:
                    "linear-gradient(135deg, #f79401 0%, #5f06a8 100%)",
                }}
              >
                <div className="d-flex justify-content-between align-items-start position-relative z-1">
                  <div>
                    <span className="opacity-75 small fw-bold text-uppercase">
                      Total Amount
                    </span>
                    <h1 className="display-6 fw-bold mb-0 mt-1">
                      ₹{selectedLog.amount}
                    </h1>
                  </div>
                  <span
                    className={`badge rounded-pill px-3 py-2 border bg-white text-${selectedLog.status_color} fw-bold`}
                  >
                    {selectedLog.status}
                  </span>
                </div>
              </div>

              {/* Details Grid */}
              <div className="bg-light rounded-4 p-3 mb-4 border border-light-subtle">
                <div className="row g-3">
                  <div className="col-12 border-bottom pb-2">
                    <small className="text-muted d-block fw-bold small text-uppercase mb-1">
                      Station Name
                    </small>
                    <span className="fw-bold text-dark">
                      {selectedLog.station_name}
                    </span>
                  </div>
                  <div className="col-4 border-end">
                    <small className="text-muted d-block fw-bold small text-uppercase">
                      Volume
                    </small>
                    <span className="fw-bold text-dark">
                      {selectedLog.liters} L
                    </span>
                  </div>
                  <div className="col-4 border-end text-center">
                    <small className="text-muted d-block fw-bold small text-uppercase">
                      Rate
                    </small>
                    <span className="fw-bold text-dark">
                      ₹{selectedLog.price_per_liter}
                    </span>
                  </div>
                  <div className="col-4 text-end">
                    <small className="text-muted d-block fw-bold small text-uppercase">
                      Date
                    </small>
                    <span className="fw-bold text-dark">
                      {new Date(selectedLog.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>

              {/* SECTION 1: DRIVER UPLOADS */}
              <h6 className="fw-bold text-muted small mb-3 text-uppercase ls-1">
                Driver Uploads
              </h6>
              <div className="row g-2 mb-4">
                {[
                  { img: selectedLog.fuel_meter_image, label: "PUMP METER" },
                  { img: selectedLog.tank_image, label: "FUEL TANK" },
                  { img: selectedLog.oil_meter_image, label: "ODOMETER" },
                  { img: selectedLog.qr_image, label: "PUMP QR" },
                ].map(
                  (item, idx) =>
                    item.img && (
                      <div className="col-6" key={idx}>
                        <div
                          className="ratio ratio-4x3 rounded-4 overflow-hidden border cursor-pointer bg-light position-relative shadow-sm"
                          onClick={() =>
                            setFullImage(getImageUrl(item.img, "driver_upload"))
                          }
                        >
                          <img
                            src={getImageUrl(item.img, "driver_upload")}
                            className="w-100 h-100 object-fit-cover"
                            alt={item.label}
                          />
                          <div
                            className="position-absolute bottom-0 w-100 bg-dark bg-opacity-50 text-white text-center py-1"
                            style={{ fontSize: "10px", fontWeight: "600" }}
                          >
                            {item.label}
                          </div>
                        </div>
                      </div>
                    ),
                )}
              </div>

              {/* ================= SECTION 2: REVAMPED PAYMENT VERIFICATION ================= */}
              {(selectedLog.payment_proof_image ||
                selectedLog.internal_notes) && (
                <div className="mt-2 mb-4">
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <h6 className="fw-bold mb-0 text-success small text-uppercase ls-1">
                      Payment Verification
                    </h6>
                  </div>

                  <div
                    className="p-3 rounded-4 border border-success-subtle bg-success"
                    style={{ "--bs-bg-opacity": ".03" }}
                  >
                    <div className="row g-3 align-items-center">
                      {/* Payment Proof Image with Playful Badge */}
                      {selectedLog.payment_proof_image && (
                        <div className="col-4">
                          <div
                            className="ratio ratio-1x1 rounded-3 overflow-hidden border border-success border-opacity-25 cursor-pointer shadow-sm position-relative"
                            onClick={() =>
                              setFullImage(
                                getImageUrl(
                                  selectedLog.payment_proof_image,
                                  "payment_proof",
                                ),
                              )
                            }
                          >
                            <img
                              src={getImageUrl(
                                selectedLog.payment_proof_image,
                                "payment_proof",
                              )}
                              className="w-100 h-100 object-fit-cover"
                              alt="Payment Proof"
                              onError={(e) =>
                                (e.target.src =
                                  "https://placehold.co/200?text=Proof")
                              }
                            />
                            <div className="position-absolute top-0 end-0 bg-dark bg-opacity-50 text-white p-1 rounded-bottom-start">
                              <Check size={12} />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Admin Note Box */}
                      <div
                        className={
                          selectedLog.payment_proof_image ? "col-8" : "col-12"
                        }
                      >
                        <div className="d-flex flex-column gap-1">
                          <span
                            className="text-success small fw-bold text-uppercase"
                            style={{ fontSize: "10px" }}
                          >
                            Confirmation Comment
                          </span>
                          <div className="bg-white p-2 rounded-3 border border-success-subtle shadow-sm">
                            <p
                              className="mb-0 text-dark fw-bold small fst-italic"
                              style={{ lineHeight: "1.4" }}
                            >
                              {selectedLog.internal_notes}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <button
                className="btn btn-dark w-100 rounded-pill py-3 fw-bold mt-2 shadow"
                onClick={() => setSelectedLog(null)}
              >
                Close Details
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============ FULL SCREEN LIGHTBOX ============ */}
      {fullImage && (
        <div
          className="fixed-top w-100 h-100 bg-black d-flex align-items-center justify-content-center p-0"
          style={{
            zIndex: 999999,
            transform: "translateZ(0)",
            WebkitTransform: "translateZ(0)",
          }}
          onClick={() => setFullImage(null)}
        >
          <button
            className="position-absolute top-0 end-0 m-4 btn btn-dark rounded-circle p-3 z-3 bg-opacity-50 border-0"
            onClick={() => setFullImage(null)}
          >
            {" "}
            <X size={24} className="text-white" />{" "}
          </button>
          <img
            src={fullImage}
            alt="Full View"
            className="img-fluid"
            style={{
              maxHeight: "100%",
              maxWidth: "100vw",
              objectFit: "contain",
            }}
          />
        </div>
      )}

      {/* --- CSS STYLES --- */}
      <style>{`
        .font-native { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
        .z-1 { z-index: 1; }
        .active-scale:active { transform: scale(0.97); transition: transform 0.1s; }
        .active-scale-down:active { transform: scale(0.98); transition: transform 0.1s; }
        .transition-all { transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1); }
        .shadow-inner { box-shadow: inset 0 2px 4px rgba(0,0,0,0.05); }
        .tracking-tight { letter-spacing: -0.5px; }
        .tracking-wide { letter-spacing: 0.5px; }
        
        /* FIX: Remove Number Input Spinners */
        .no-spinners::-webkit-outer-spin-button,
        .no-spinners::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type="number"].no-spinners {
          -moz-appearance: textfield;
        }
        
        /* Modal Animations */
        .animate-slide-down { animation: slideDown 0.3s ease-out; }
        @keyframes slideDown { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
        
        .animate-slide-up { 
           animation: slideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
           -webkit-animation: slideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        @keyframes slideUp { 
          from { 
            transform: translateY(100%); 
            -webkit-transform: translateY(100%);
          } 
          to { 
            transform: translateY(0); 
            -webkit-transform: translateY(0);
          } 
        }
      `}</style>
    </div>
  );
};

export default RefuelPage;